import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MaterialModule } from '../../../shared/material.module';
import { LargeShareholderService } from '../../../services/large-shareholder.service';
import { PyramidalShareholderService } from '../../../services/pyramidal-shareholder.service';
import { Helpers } from '../../../utilities/helpers';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { UpsertMultiYearPyramidRequest, YearPercentage } from '../../../models/api/upsertMultiYearPyramidRequest';
import { CreatePyramidalShareholderRequest } from '../../../models/api/createPyramidalShareholderRequest';
import { HotToastService } from '@ngxpert/hot-toast';
import { MatDialogRef } from '@angular/material/dialog';
import { switchMap } from 'rxjs';
import { FormPyramidItemComponent } from '../form-pyramid-item/form-pyramid-item.component';

@Component({
  selector: 'app-add-pyramid-item',
  imports: [
    CommonModule,
    NgxSpinnerModule,
    MaterialModule,
    FormPyramidItemComponent
  ],
  templateUrl: './add-pyramid-item.component.html',
  styleUrl: './add-pyramid-item.component.scss'
})
export class AddPyramidItemComponent implements OnInit {
  @ViewChild(FormPyramidItemComponent) formComponent!: FormPyramidItemComponent;

  @Input() prefilledName?: string;
  @Input() prefilledCategoryId?: string;
  @Input() prefilledSubCategoryId?: string;
  @Input() largeShareholderId!: string;
  @Input() connectedEntityId?: string;
  @Input() connectedEntityType?: 'largeShareholder' | 'pyramidalShareholder' | 'company' | 'opbCompany' | 'director';
  @Input() isCreatingNewPyramidalShareholder: boolean = false;
  @Input() dialogRef?: MatDialogRef<any>;
  @Output() formValidityChanged = new EventEmitter<boolean>();

  measurements: string[] = [];

  private largeShareholderService = inject(LargeShareholderService);
  private pyramidalShareholderService = inject(PyramidalShareholderService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private confirmationDialogService = inject(ConfirmationDialogService);

  constructor() {
    // Load years from measurements helper
    this.measurements = Helpers.measurements();
  }

  ngOnInit(): void {
  }

  onFormValidityChanged(isValid: boolean): void {
    this.formValidityChanged.emit(isValid);
  }

  onConfirm(): void {
    if (!this.formComponent.isValid()) {
      this.toast.error('Please fill in all required fields');
      return;
    }

    const dialogObservable = this.confirmationDialogService.openCreateConfirmationDialog();

    dialogObservable.subscribe((result: boolean) => {
      if (result) {
        this.submitPyramid();
      }
    });
  }

  private submitPyramid(): void {
    const formData = this.formComponent.getFormValue();

    // Extract year-percentage pairs from the form
    const yearPercentages: YearPercentage[] = [];
    this.measurements.forEach(measurement => {
      const percentage = formData[`directPercentage_${measurement}`];
      if (percentage !== null && percentage !== undefined && percentage !== '') {
        yearPercentages.push({
          year: parseInt(measurement, 10),
          percentage: parseFloat(percentage)
        });
      }
    });

    this.spinnerService.show();

    // If we're creating a new pyramidal shareholder, do that first
    if (this.isCreatingNewPyramidalShareholder) {
      this.createPyramidalShareholderThenPyramid(formData, yearPercentages);
    } else {
      // Otherwise, just create the pyramid entries
      this.createPyramidEntries(formData, yearPercentages, this.connectedEntityId, this.connectedEntityType);
    }
  }

  private createPyramidalShareholderThenPyramid(formData: any, yearPercentages: YearPercentage[]): void {
    // Build request to create pyramidal shareholder
    const createPyramidalShareholderRequest: CreatePyramidalShareholderRequest = {
      name: formData.pyramidName,
      categoryId: formData.category?.id || '',
      subCategoryId: formData.subcategory?.id || '',
      notes: ''
    };

    // First, create the pyramidal shareholder
    this.pyramidalShareholderService.createPyramidalShareholder(createPyramidalShareholderRequest)
      .pipe(
        switchMap(pyramidalShareholder => {
          // Then create pyramid entries using the newly created pyramidal shareholder ID
          return this.createPyramidEntriesObservable(formData, yearPercentages, pyramidalShareholder.id, 'pyramidalShareholder');
        })
      )
      .subscribe({
        next: (pyramids) => {
          this.spinnerService.hide();
          this.toast.success('Pyramidal shareholder and pyramid entries added successfully', {
            iconTheme: {
              primary: 'var(--mat-sys-primary)',
              secondary: '#FFF',
            }
          });

          // Close the dialog and return the result
          if (this.dialogRef) {
            this.dialogRef.close({ success: true, pyramids });
          }
        },
        error: (error) => {
          this.spinnerService.hide();
          console.error('Error creating pyramidal shareholder and pyramid entries:', error);
          this.toast.error('Failed to create pyramidal shareholder and pyramid entries');
        }
      });
  }

  private createPyramidEntries(
    formData: any,
    yearPercentages: YearPercentage[],
    entityId?: string,
    entityType?: 'largeShareholder' | 'pyramidalShareholder' | 'company' | 'opbCompany' | 'director'
  ): void {
    this.createPyramidEntriesObservable(formData, yearPercentages, entityId, entityType).subscribe({
      next: (pyramids) => {
        this.spinnerService.hide();
        this.toast.success('Pyramid entries added successfully', {
          iconTheme: {
            primary: 'var(--mat-sys-primary)',
            secondary: '#FFF',
          }
        });

        // Close the dialog and return the result
        if (this.dialogRef) {
          this.dialogRef.close({ success: true, pyramids });
        }
      },
      error: (error) => {
        this.spinnerService.hide();
        console.error('Error adding pyramid entries:', error);
        this.toast.error('Failed to add pyramid entries');
      }
    });
  }

  private createPyramidEntriesObservable(
    formData: any,
    yearPercentages: YearPercentage[],
    entityId?: string,
    entityType?: 'largeShareholder' | 'pyramidalShareholder' | 'company' | 'opbCompany' | 'director'
  ) {
    // Build the request object
    const request: UpsertMultiYearPyramidRequest = {
      largeShareholderId: this.largeShareholderId,
      categoryId: formData.category?.id,
      subCategoryId: formData.subcategory?.id,
      yearPercentages: yearPercentages
    };

    // Set the appropriate entity ID based on the connection type
    if (entityId && entityType) {
      switch (entityType) {
        case 'largeShareholder':
          request.pyramidLargeShareholderId = entityId;
          break;
        case 'pyramidalShareholder':
          request.pyramidalShareholderId = entityId;
          break;
        case 'company':
          request.companyId = entityId;
          break;
        case 'opbCompany':
          request.opbCompanyId = entityId;
          break;
        case 'director':
          request.directorId = entityId;
          break;
      }
    }

    return this.largeShareholderService.upsertMultiYearPyramid(request);
  }
}
